import React, { useState, useRef, useEffect } from 'react';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

/**
 * SearchBar Component
 * 
 * Search and replace functionality for transcript
 */
const SearchBar = ({
  onSearch,
  onReplace,
  onNext,
  onPrevious,
  results = [],
  currentIndex = 0,
  isOpen = false,
  onClose,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [replaceQuery, setReplaceQuery] = useState('');
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [showReplace, setShowReplace] = useState(false);
  const searchInputRef = useRef(null);

  /**
   * Handle search
   */
  const handleSearch = (query) => {
    setSearchQuery(query);
    onSearch(query, caseSensitive);
  };

  /**
   * Handle replace
   */
  const handleReplace = () => {
    onReplace(searchQuery, replaceQuery, false, caseSensitive);
  };

  /**
   * Handle replace all
   */
  const handleReplaceAll = () => {
    onReplace(searchQuery, replaceQuery, true, caseSensitive);
  };

  /**
   * Focus input when opened
   */
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  /**
   * Update search when case sensitivity changes
   */
  useEffect(() => {
    if (searchQuery) {
      onSearch(searchQuery, caseSensitive);
    }
  }, [caseSensitive]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  return (
    <div className="te-search">
      <div className="te-search-row">
        <div className="te-search-input-wrap">
          <MagnifyingGlassIcon className="te-search-icon" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search in transcript..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                if (e.shiftKey) {
                  onPrevious();
                } else {
                  onNext();
                }
              }
            }}
            className="te-search-input"
          />
        </div>

        <div className="te-search-nav">
          <span className="te-search-count">
            {results.length > 0 ? `${currentIndex + 1} / ${results.length}` : '0 / 0'}
          </span>
          <button
            onClick={onPrevious}
            disabled={results.length === 0}
            className="te-icon-btn"
            title="Previous (Shift+Enter)"
          >
            <ChevronUpIcon className="h-4 w-4" />
          </button>
          <button
            onClick={onNext}
            disabled={results.length === 0}
            className="te-icon-btn"
            title="Next (Enter)"
          >
            <ChevronDownIcon className="h-4 w-4" />
          </button>
        </div>

        <button
          onClick={() => setShowReplace(!showReplace)}
          className="te-btn te-btn-ghost"
        >
          {showReplace ? 'Hide replace' : 'Replace'}
        </button>

        <button
          onClick={onClose}
          className="te-icon-btn"
          title="Close (Esc)"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>

      {showReplace && (
        <div className="te-search-row">
          <input
            type="text"
            placeholder="Replace with..."
            value={replaceQuery}
            onChange={(e) => setReplaceQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleReplace();
              }
            }}
            className="te-search-input"
            style={{ paddingLeft: 12 }}
          />
          <button
            onClick={handleReplace}
            disabled={!searchQuery || results.length === 0}
            className="te-btn te-btn-secondary"
          >
            Replace
          </button>
          <button
            onClick={handleReplaceAll}
            disabled={!searchQuery || results.length === 0}
            className="te-btn te-btn-secondary"
          >
            Replace all
          </button>
        </div>
      )}

      <div className="te-search-options">
        <label className="te-search-option">
          <input
            type="checkbox"
            checked={caseSensitive}
            onChange={(e) => setCaseSensitive(e.target.checked)}
          />
          Case sensitive
        </label>
      </div>
    </div>
  );
};

export default SearchBar;


